import {
  Button,
  Switch,
  Tabs,
  useToaster
} from "@dealer-center/core-components";
import React, { useEffect, useRef, useState } from "react";
import type {
  ModelImageRow,
  TrimMap,
  TrimViewData,
  TrimViewProps
} from "@dealer-center/inventory-pages-gvm-interface";
import {
  trimInputIdPrefix,
  trimValidationPrefix
} from "../../util/trimDataMapper";
import { toTrimViewData } from "../../util/vehicleDataMapper";
import { formatLocale } from "../../util/localeFormatter";
import { useIsGvmRwAccessUser } from "../../util/gvmUserUtil";
import { generateKeys } from "../../util/compKeyGenerator";
import { TrimDetailView } from "./TrimDetailView";
import { TrimMapTable } from "../../Common";
import {
  useDownloadAutodataImages,
  useEvictCache,
  useFlushTrimImages,
  useTrimMapDelete,
  useTrimMapSave,
  useTrimSave,
  useTrimShowroomStateUpdate
} from "@dealer-center/inventory-pages-gvm-fetch";
import { Link } from "react-router-dom";
import { ColorTable, ImageTable } from "../../Common";

export const TrimView = ({
  locale,
  trimId,
  closeTrimView,
  trimWithDetailsQuery
}: TrimViewProps) => {
  const isGvmRwAccessUser = useIsGvmRwAccessUser();
  const [activeTab, setActiveTab] = useState("details");
  const trimViewData: TrimViewData = toTrimViewData(trimWithDetailsQuery.data);
  const saveTrimMapMutation = useTrimMapSave(locale, trimId);
  const deleteTrimMapMutation = useTrimMapDelete(locale, trimId);

  const updateTrimShowroomMutation = useTrimShowroomStateUpdate({
    trimId,
    locale,
    toInclude: !trimWithDetailsQuery.data?.includedInShowroom
  });

  const inputRefs = useRef<Record<string, string>>({});
  const previousIsFetching = useRef(false);
  const [inputKeys, setInputKeys] = useState<Record<string, string>>(() =>
    generateKeys(trimViewData.validFields)
  );
  const [saveButtonEnabled, setSaveButtonEnabled] = useState(false);
  const saveMutation = useTrimSave();
  const flushMutation = useEvictCache();
  const downloadAutoDataImagesMutation = useDownloadAutodataImages(
    locale,
    trimId
  );
  const flushTrimImagesMutation = useFlushTrimImages();
  const { toastError } = useToaster();
  const vehicle = trimWithDetailsQuery.data?.vehicle;
  const formattedLocale = formatLocale(locale);
  const [selectedImageRows, setSelectedImageRows] = useState<ModelImageRow[]>(
    []
  );
  let saveLabel = "";

  if (trimViewData.qa) {
    saveLabel = saveMutation.isPending ? "Promoting..." : "Promote Changes";
  } else {
    saveLabel = saveMutation.isPending ? "Saving..." : "Save Changes";
  }

  useEffect(() => {
    if (
      previousIsFetching.current &&
      !trimWithDetailsQuery.isFetching &&
      !trimWithDetailsQuery.error
    ) {
      setInputKeys(generateKeys(trimViewData.validFields));
      setSaveButtonEnabled(false);
    }
    previousIsFetching.current = trimWithDetailsQuery.isFetching;
  }, [trimWithDetailsQuery.isFetching]);

  const isShowRefreshing = () => {
    return (
      saveTrimMapMutation.isPending ||
      deleteTrimMapMutation.isPending ||
      saveMutation.isPending ||
      trimWithDetailsQuery.isLoading ||
      trimWithDetailsQuery.isFetching ||
      updateTrimShowroomMutation.isPending ||
      flushMutation.isPending ||
      downloadAutoDataImagesMutation.isPending ||
      flushTrimImagesMutation.isPending
    );
  };
  const isElementDisabled = !isGvmRwAccessUser || isShowRefreshing();

  const handleTabClick = (tabValue: string) => {
    setActiveTab(tabValue);
  };

  const handleUpdateTrimShowroom = () => {
    updateTrimShowroomMutation.mutate({
      vehicleId: vehicle?.globalVehicleId || "",
      trimId,
      locale,
      make: vehicle?.make || ""
    });
  };

  const handleAdd = (record: TrimMap) => {
    saveTrimMapMutation.mutate({
      ...record,
      globalVehicleTrimId: trimId
    });
  };

  const handleDelete = (record: TrimMap) => {
    deleteTrimMapMutation.mutate(record.recordId);
  };

  const handleTrimSave = () => {
    const updates: Record<string, string> = {
      locale,
      trimId,
      vehicleId: trimViewData.viewItem.globalVehicleId
    };

    for (const field in inputRefs.current) {
      if (field.startsWith(trimValidationPrefix)) {
        const validationResult = inputRefs.current[field];

        if (validationResult === "exceedsMaxLength") {
          const sourceField = field.substring(trimValidationPrefix.length);
          document
            .getElementById(`${trimInputIdPrefix}${sourceField}`)
            ?.focus();
          toastError("Input exceeds character limit");
          return;
        }
      } else {
        updates[field] = inputRefs.current[field];
      }
    }

    saveMutation.mutate(updates);
  };

  const handleVehicleFlush = () => {
    flushMutation.mutate({
      vehicleIds: [],
      trimIds: [trimId]
    });
  };

  const handleDownloadAutodataImages = () => {
    downloadAutoDataImagesMutation.mutate(trimId);
  };

  const handleFlushSelectedImages = () => {
    const urls = selectedImageRows.map(row => row.url);
    flushTrimImagesMutation.mutate({
      trimId,
      urls
    });
  };

  const render = () => {
    return (
      <div style={{ position: "relative", width: "100%" }}>
        <div style={{ textAlign: "left" }}>
          <h2>
            {vehicle?.year} {vehicle?.make} {vehicle?.model}{" "}
            {vehicle?.bodyStyle} {trimViewData?.editItem.uniqueTrim}
          </h2>
          <p id="trimVehicleId" style={{ marginTop: "25px" }}>
            <b>Vehicle Id:</b>{" "}
            {!isElementDisabled ? (
              <Link
                rel="noopener noreferrer"
                to={`/gvm/vehicle?id=${vehicle?.globalVehicleId}&locale=${locale}`}
              >
                {vehicle?.globalVehicleId}
              </Link>
            ) : (
              <span>{vehicle?.globalVehicleId}</span>
            )}
          </p>
          <p id="trimTrimId" style={{ marginTop: "25px" }}>
            <b>Trim Id:</b> {trimId}
          </p>
          <p id="trimLocale">
            <b>Locale:</b> {formattedLocale}
          </p>
          <p id="trimStatus">
            <b>Status:</b>{" "}
            {trimViewData?.editItem.active ? "Active" : "Inactive"}
          </p>
          <p id="trimInShowroom">
            <b>In Showroom:</b>{" "}
            <Switch
              checked={trimWithDetailsQuery.data?.includedInShowroom}
              disabled={isElementDisabled}
              id="trimInShowroomSwitch"
              layout="vertical"
              onClick={handleUpdateTrimShowroom}
            />
          </p>
        </div>
        <Tabs
          id="TrimTabs"
          tabs={[
            {
              component: (
                <TrimDetailView
                  availableModels={
                    trimWithDetailsQuery.data?.availableModels ?? {}
                  }
                  inputKeys={inputKeys}
                  inputRefs={inputRefs}
                  isLoading={isShowRefreshing()}
                  setSaveButtonEnabled={setSaveButtonEnabled}
                  trimViewData={trimViewData}
                />
              ),
              label: "Details",
              value: "details",
              active: activeTab === "details"
            },
            {
              component: (
                <TrimMapTable
                  handleAdd={handleAdd}
                  handleDelete={handleDelete}
                  isLoading={isShowRefreshing()}
                  trimMapList={trimWithDetailsQuery.data?.trimMapList || []}
                />
              ),
              label: "Mapping",
              value: "mapping",
              active: activeTab === "mapping"
            },
            {
              component: (
                <ImageTable
                  images={trimWithDetailsQuery?.data?.trimImageList ?? []}
                  isLoading={isShowRefreshing()}
                  setSelectedImageRows={setSelectedImageRows}
                  shotTypeLookup={trimWithDetailsQuery?.data?.shotTypes ?? {}}
                />
              ),
              label: "Images",
              value: "images",
              active: activeTab === "images",
              "data-testid": "imagesTab"
            },
            {
              component: (
                <ColorTable
                  colors={trimWithDetailsQuery?.data?.trimColorList ?? []}
                  isLoading={isShowRefreshing()}
                  shotTypeLookup={trimWithDetailsQuery?.data?.shotTypes ?? {}}
                />
              ),
              label: "Colors",
              value: "colors",
              active: activeTab === "colors",
              "data-testid": "colorsTab"
            }
          ]}
          onActivate={handleTabClick}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            marginTop: "20px"
          }}
        >
          <Button
            analyticsConfiguration={{
              eventName: "Close trim view clicked"
            }}
            buttonStyle="tertiary"
            data-testid="trim-edit-cancel"
            type="button"
            onClick={closeTrimView}
          >
            Back
          </Button>
          <Button
            analyticsConfiguration={{
              eventName: "Flush trim Images"
            }}
            buttonStyle="secondary"
            data-testid="trim-flush-images"
            disabled={selectedImageRows.length === 0 || isElementDisabled}
            type="button"
            onClick={handleFlushSelectedImages}
          >
            {flushTrimImagesMutation.isPending
              ? "Flushing..."
              : "Flush Selected Images"}
          </Button>
          {(activeTab === "images" || activeTab === "colors") && (
            <Button
              analyticsConfiguration={{
                eventName: "redownload autodata images"
              }}
              buttonStyle="secondary"
              data-testid="trim-redownload-images"
              disabled={isElementDisabled}
              id="redownloadAutodataImagesButton"
              type="button"
              onClick={handleDownloadAutodataImages}
            >
              {downloadAutoDataImagesMutation.isPending
                ? "Redownloading..."
                : "Redownload Autodata Images"}
            </Button>
          )}
          <Button
            analyticsConfiguration={{
              eventName: "Flush caches clicked"
            }}
            buttonStyle="secondary"
            data-testid="trim-edit-flush"
            disabled={isElementDisabled}
            type="button"
            onClick={handleVehicleFlush}
          >
            {flushMutation.isPending ? "Flushing..." : "Flush Caches"}
          </Button>
          {activeTab === "details" && (
            <Button
              analyticsConfiguration={{
                eventName: "Save changes clicked"
              }}
              buttonStyle="primary"
              data-testid="trim-edit-save"
              disabled={!saveButtonEnabled || isShowRefreshing()}
              type="button"
              onClick={handleTrimSave}
            >
              {saveLabel}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return render();
};
